import { useState, useEffect, useCallback } from 'react';
import { Settings, Trash2, Clock, ChevronDown, ChevronUp, BarChart2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import waterService, { DailySummary, WeeklyStats } from '../services/waterService';

// ============================================
// CIRCULAR PROGRESS RING
// ============================================
function CircularProgress({ percentage, totalMl, goalMl }: { percentage: number; totalMl: number; goalMl: number }) {
  const radius = 90;
  const stroke = 10;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (Math.min(percentage, 100) / 100) * circumference;

  const formatLiters = (ml: number) => {
    if (ml >= 1000) return `${(ml / 1000).toFixed(1).replace('.0', '')} L`;
    return `${ml} ml`;
  };

  return (
    <div className="flex flex-col items-center justify-center py-6">
      <div className="relative" style={{ width: radius * 2, height: radius * 2 }}>
        <svg width={radius * 2} height={radius * 2} className="rotate-[-90deg]">
          {/* Background ring */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={stroke}
          />
          {/* Progress ring */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="none"
            stroke={percentage >= 100 ? '#10b981' : '#60a5fa'}
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease' }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-gray-900">{percentage}%</span>
          <span className="text-sm text-gray-500 mt-1">
            {formatLiters(totalMl)} din {formatLiters(goalMl)}
          </span>
        </div>
      </div>
      {percentage >= 100 && (
        <div className="mt-3 flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
          <span>🎉</span>
          <span>Obiectiv atins! Felicitări!</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// SETTINGS MODAL
// ============================================
function SettingsModal({
  isOpen,
  onClose,
  currentGoal,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentGoal: number;
  onSave: (goalMl: number) => void;
}) {
  const [goalLiters, setGoalLiters] = useState((currentGoal / 1000).toFixed(1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGoalLiters((currentGoal / 1000).toFixed(1));
  }, [currentGoal]);

  const presets = [1.5, 2, 2.5, 3];

  const handleSave = async () => {
    const ml = Math.round(parseFloat(goalLiters) * 1000);
    if (isNaN(ml) || ml < 500 || ml > 10000) {
      toast.error('Obiectivul trebuie să fie între 0.5L și 10L');
      return;
    }
    setSaving(true);
    try {
      await onSave(ml);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Setări aport apă</h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Obiectiv zilnic
            </label>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="number"
                value={goalLiters}
                onChange={e => setGoalLiters(e.target.value)}
                step="0.1"
                min="0.5"
                max="10"
                className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-600 font-medium">litri</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {presets.map(l => (
                <button
                  key={l}
                  onClick={() => setGoalLiters(l.toFixed(1))}
                  className={`py-2 rounded-xl text-sm font-medium transition-colors ${
                    parseFloat(goalLiters) === l
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {l}L
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Anulează
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors disabled:opacity-60"
            >
              {saving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CUSTOM AMOUNT MODAL
// ============================================
function CustomAmountModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (amount: number) => void;
}) {
  const [amount, setAmount] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    const ml = parseInt(amount);
    if (isNaN(ml) || ml < 1 || ml > 5000) {
      toast.error('Introdu o cantitate validă (1-5000 ml)');
      return;
    }
    setAdding(true);
    try {
      await onAdd(ml);
      setAmount('');
      onClose();
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Cantitate personalizată</h2>
          <div className="flex items-center gap-2 mb-6">
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="ex: 350"
              min="1"
              max="5000"
              autoFocus
              className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-600 font-medium">ml</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Anulează
            </button>
            <button
              onClick={handleAdd}
              disabled={adding}
              className="flex-1 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors disabled:opacity-60"
            >
              {adding ? 'Se adaugă...' : 'Adaugă'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// WEEKLY CHART
// ============================================
function WeeklyChart({ stats }: { stats: WeeklyStats }) {
  const days = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
  const maxMl = Math.max(...stats.days.map(d => d.goalMl), 100);

  const getDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    return days[date.getDay() === 0 ? 6 : date.getDay() - 1];
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 mt-4">
      <div className="flex items-center gap-2 mb-4">
        <BarChart2 className="w-5 h-5 text-blue-500" />
        <h3 className="font-semibold text-gray-900">Ultimele 7 zile</h3>
      </div>

      <div className="flex items-end gap-2 h-24 mb-2">
        {stats.days.map((day, i) => {
          const heightPct = Math.min((day.totalMl / maxMl) * 100, 100);
          const isToday = i === stats.days.length - 1;
          const goalMet = day.totalMl >= day.goalMl;
          return (
            <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
                <div
                  className={`w-full rounded-t-lg transition-all duration-500 ${
                    goalMet ? 'bg-emerald-400' : isToday ? 'bg-blue-500' : 'bg-blue-200'
                  }`}
                  style={{ height: `${Math.max(heightPct, 4)}%` }}
                />
              </div>
              <span className={`text-[10px] font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                {getDayLabel(day.date)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
        <span>Medie: <strong className="text-gray-700">{(stats.summary.avgDailyMl / 1000).toFixed(1)}L</strong></span>
        <span>Obiectiv atins: <strong className="text-gray-700">{stats.summary.daysGoalMet}/7 zile</strong></span>
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================
export default function WaterIntakePage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingAmount, setAddingAmount] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const quickAmounts = [250, 500, 750];

  const loadData = useCallback(async () => {
    try {
      const [summaryData, weeklyData] = await Promise.all([
        waterService.getTodaySummary(),
        waterService.getWeeklyStats(),
      ]);
      setSummary(summaryData);
      setWeeklyStats(weeklyData);
    } catch (err: any) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddWater = async (amount: number) => {
    setAddingAmount(amount);
    try {
      const result = await waterService.addWater(amount);
      setSummary(result.summary);
      toast.success(`+${amount} ml adăugați! 💧`);
      // Refresh weekly stats
      waterService.getWeeklyStats().then(setWeeklyStats).catch(() => {});
    } catch (err: any) {
      toast.error(err.message || 'Eroare la adăugare');
    } finally {
      setAddingAmount(null);
    }
  };

  const handleDeleteLog = async (id: string) => {
    setDeletingId(id);
    try {
      const result = await waterService.deleteLog(id);
      setSummary(result.summary);
      toast.success('Înregistrare ștearsă');
    } catch (err: any) {
      toast.error(err.message || 'Eroare la ștergere');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateGoal = async (goalMl: number) => {
    try {
      await waterService.updateGoal(goalMl);
      toast.success('Obiectiv actualizat!');
      await loadData();
    } catch (err: any) {
      toast.error(err.message || 'Eroare la actualizare');
      throw err;
    }
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('ro-RO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div
        className="bg-white shadow-sm border-b border-gray-200 sticky z-10"
        style={{ top: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between min-h-16 py-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex items-center gap-2">
                {/* Water drop icon */}
                <svg className="w-7 h-7 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
                  <path d="M12 2.1L6.5 9.5C5.5 11 5 12.5 5 14c0 3.87 3.13 7 7 7s7-3.13 7-7c0-1.5-.5-3-1.5-4.5L12 2.1z" />
                </svg>
                <h1 className="text-lg font-bold text-gray-900">Aport apă</h1>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-6 h-6 text-purple-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Circular Progress */}
          <CircularProgress
            percentage={summary?.percentage ?? 0}
            totalMl={summary?.totalMl ?? 0}
            goalMl={summary?.goalMl ?? 2000}
          />

          {/* Quick Add Buttons */}
          <div className="px-5 pb-6">
            <div className="grid grid-cols-3 gap-3 mb-3">
              {quickAmounts.map(amount => (
                <button
                  key={amount}
                  onClick={() => handleAddWater(amount)}
                  disabled={addingAmount !== null}
                  className={`
                    py-4 rounded-2xl font-semibold text-white text-base transition-all
                    shadow-md active:scale-95
                    ${addingAmount === amount
                      ? 'bg-purple-400 scale-95'
                      : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200'
                    }
                    disabled:opacity-70
                  `}
                >
                  {addingAmount === amount ? (
                    <span className="flex items-center justify-center gap-1">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </span>
                  ) : (
                    `+ ${amount} ml`
                  )}
                </button>
              ))}
            </div>

            {/* Custom amount button */}
            <button
              onClick={() => setShowCustom(true)}
              className="w-full py-3 rounded-2xl border-2 border-dashed border-gray-300 text-gray-500 font-medium text-sm hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              + Cantitate personalizată
            </button>
          </div>
        </div>

        {/* Weekly Stats Toggle */}
        <button
          onClick={() => setShowWeekly(!showWeekly)}
          className="w-full mt-4 bg-white rounded-2xl border border-gray-200 px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-blue-500" />
            <span className="font-medium text-gray-800">Statistici săptămânale</span>
          </div>
          {showWeekly ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showWeekly && weeklyStats && (
          <WeeklyChart stats={weeklyStats} />
        )}

        {/* Today's History Toggle */}
        {(summary?.logs?.length ?? 0) > 0 && (
          <>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full mt-4 bg-white rounded-2xl border border-gray-200 px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="font-medium text-gray-800">
                  Istoric azi ({summary?.logs?.length} înregistrări)
                </span>
              </div>
              {showHistory ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {showHistory && (
              <div className="bg-white rounded-2xl border border-gray-200 mt-1 overflow-hidden">
                {[...(summary?.logs ?? [])].reverse().map((log, index) => (
                  <div
                    key={log.id}
                    className={`flex items-center justify-between px-5 py-3 ${
                      index < (summary?.logs?.length ?? 0) - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.1L6.5 9.5C5.5 11 5 12.5 5 14c0 3.87 3.13 7 7 7s7-3.13 7-7c0-1.5-.5-3-1.5-4.5L12 2.1z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{log.amount} ml</p>
                        <p className="text-xs text-gray-500">{formatTime(log.loggedAt)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteLog(log.id)}
                      disabled={deletingId === log.id}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deletingId === log.id ? (
                        <span className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin block" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Remaining info */}
        {(summary?.remainingMl ?? 0) > 0 && (
          <div className="mt-4 bg-blue-50 rounded-2xl px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.1L6.5 9.5C5.5 11 5 12.5 5 14c0 3.87 3.13 7 7 7s7-3.13 7-7c0-1.5-.5-3-1.5-4.5L12 2.1z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-800">
                Mai ai nevoie de{' '}
                {summary!.remainingMl >= 1000
                  ? `${(summary!.remainingMl / 1000).toFixed(1)}L`
                  : `${summary!.remainingMl} ml`}
              </p>
              <p className="text-xs text-blue-600">pentru a-ți atinge obiectivul de azi</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentGoal={summary?.goalMl ?? 2000}
        onSave={handleUpdateGoal}
      />

      <CustomAmountModal
        isOpen={showCustom}
        onClose={() => setShowCustom(false)}
        onAdd={handleAddWater}
      />
    </div>
  );
}
